import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, Spinner, Image, Row, Col, Badge, Button, Modal } from "react-bootstrap";
import { FaWhatsapp, FaCog, FaCheckCircle, FaSignOutAlt } from "react-icons/fa";
import { IoMdMenu } from "react-icons/io";
import Cookies from "universal-cookie";

function WhatsApp() {
  const [qrCode, setQrCode] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef(null);
  const cookies = new Cookies();
  const [timeAgo, setTimeAgo] = useState("—");

  const [InstanceName, setInstanceName] = useState("—");
  const [apiKey, setAPikey] = useState("—");
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const WHATSAPP_URL = cookies.get("baseUrlWhatsApp");
  const getApiHeaders = () => ({
    headers: {
      apikey: apiKey,
      "Content-Type": "application/json",
    },
  });

  const fetchQRCode = async (isInitial = false) => {
    try {
      if (isInitial) setIsLoading(true);

      const result = await axios.post(`${window.$url}/WhatsApp/ConnectWAInstance`, null);
      const response = result.data || {};
      const syncDate = new Date();

      if (response.IsSuccess) {
        const { QRCode, InstanceName, ApiKey } = response.Data || {};

        if (QRCode) {
          setQrCode(QRCode);
          setIsConnected(false);
        } else if (ApiKey) {
          setIsConnected(true);
          setQrCode(null);
          setAPikey(ApiKey);
          setInstanceName(InstanceName);
        } else {
          setQrCode(null);
          setIsConnected(false);
        }
      } else {
        setQrCode(null);
        setIsConnected(false);
        toast.error(response.Message || "Failed to fetch WhatsApp status.");
      }

      setTimeAgo(
        syncDate.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        })
      );

      if (isInitial) setIsLoading(false);
    } catch (error) {
      if (isInitial) setIsLoading(false);
      console.error("QR Fetch Error:", error);

      const errMsg =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        "Something went wrong while fetching QR code!";
      toast.error(errMsg);
    }
  };

  const handleLogout = () => setShowLogoutModal(true);
  const handleCloseModal = () => setShowLogoutModal(false);

  const confirmLogout = async () => {
    handleCloseModal();
    setIsLoading(true);
    try {
      const logoutUrl = `${WHATSAPP_URL}instance/logout/${InstanceName}`;
      await axios.delete(logoutUrl, getApiHeaders());

      setQrCode(null);
      setIsConnected(false);
      toast.info("WhatsApp instance logged out. Fetching new QR code...");
      await fetchQRCode(false);
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Failed to log out the WhatsApp instance.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQRCode(true);
    intervalRef.current = setInterval(() => fetchQRCode(false), 10000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const renderQrContent = () => {
    if (isLoading) {
      return (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted small">Loading QR code...</p>
        </div>
      );
    }

    if (isConnected) {
      return (
        <div className="text-center py-2 px-3">
          <div
            className="mx-auto p-3 rounded-circle bg-success text-white d-inline-block connected-status mb-3"
            style={{ width: "80px", height: "80px" }}
          >
            <FaCheckCircle style={{ fontSize: "3rem" }} />
          </div>
          <p className="fw-bold text-success mb-2">Connected</p>
          <p className="lead text-secondary small mb-3">
            Your WhatsApp is now connected and ready to use!
            <br />
            <span style={{ fontSize: "0.75rem", fontWeight: "normal" }}>
              (The system will continuously update this status every 10 seconds.)
            </span>
          </p>
          <Button variant="danger" onClick={handleLogout} size="sm" disabled={isLoading}>
            {isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <FaSignOutAlt className="me-1" /> Logout
              </>
            )}
          </Button>
        </div>
      );
    }

    if (qrCode) {
      return (
        <>
          <Image
            src={qrCode}
            alt="WhatsApp QR Code"
            style={{
              maxWidth: "250px",
              borderRadius: "10px",
              border: "1px solid #e0e0e0",
            }}
            fluid
          />
          <p className="text-muted mt-2 small">Last sync: {timeAgo}</p>
        </>
      );
    }

    return (
      <div className="text-center p-5 text-muted">
        <p>No QR Code available</p>
        <p className="small">Last sync: {timeAgo}</p>
      </div>
    );
  };

  return (
    <>
      <Card style={{ maxWidth: "800px", margin: "20px auto" }}>
        <Card.Header as="h5" className="text-center">
          <FaWhatsapp color="#25D366" className="me-2" style={{ fontSize: "1.5em" }} />
          WhatsApp Login Status
        </Card.Header>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={7}>
              <h4 className="mb-4">Steps to log in</h4>
              <div className="steps-container">
                <div className="step-item">
                  <span className="step-number">1</span> Open WhatsApp <FaWhatsapp /> on your phone
                </div>
                <div className="step-item">
                  <span className="step-number">2</span> On Android tap Menu <IoMdMenu /> · On iPhone tap Settings{" "}
                  <FaCog />
                </div>
                <div className="step-item">
                  <span className="step-number">3</span> Tap Linked devices, then Link device
                </div>
                <div className="step-item">
                  <span className="step-number">4</span> Scan the QR code to confirm
                </div>
              </div>
              <p className="text-secondary small mt-4">
                Status Last Checked:{" "}
                <Badge bg="light" text="dark">
                  {timeAgo}
                </Badge>
              </p>
            </Col>

            <Col md={5} className="text-center">
              <Card
                className="p-3"
                style={{
                  border: isLoading ? "1px solid #0d6efd" : isConnected ? "1px solid #198754" : "1px solid #e0e0e0",
                  minHeight: "280px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isConnected ? "#f6fff8" : "#fff",
                }}
              >
                {renderQrContent()}
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Logout Confirmation Modal */}
      <Modal show={showLogoutModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm WhatsApp Logout</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to <strong>log out</strong> the current WhatsApp instance? You will need to scan a new
          QR code to reconnect.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: "#dc3545", color: "#fff", border: "none" }}
            onClick={confirmLogout}
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <FaSignOutAlt className="me-1" /> Yes, Logout
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default WhatsApp;
